import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { HeroSection } from "@/components/sections/HeroSection";
import { buildSeo, organizationJsonLd, websiteJsonLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageProvider";

const AboutIntroSection = lazy(() => import("@/components/sections/AboutIntroSection").then((m) => ({ default: m.AboutIntroSection })));
const ServicesGrid = lazy(() => import("@/components/sections/ServicesGrid").then((m) => ({ default: m.ServicesGrid })));
const PortfolioSection = lazy(() => import("@/components/sections/PortfolioSection").then((m) => ({ default: m.PortfolioSection })));
const WhyUsSection = lazy(() => import("@/components/sections/WhyUsSection").then((m) => ({ default: m.WhyUsSection })));
const StrategySection = lazy(() => import("@/components/sections/StrategySection").then((m) => ({ default: m.StrategySection })));
const ProcessSection = lazy(() => import("@/components/sections/ProcessSection").then((m) => ({ default: m.ProcessSection })));
const StatsBanner = lazy(() => import("@/components/sections/StatsBanner").then((m) => ({ default: m.StatsBanner })));
const ActionCardsSection = lazy(() => import("@/components/sections/ActionCardsSection").then((m) => ({ default: m.ActionCardsSection })));
const TestimonialsSection = lazy(() => import("@/components/sections/TestimonialsSection").then((m) => ({ default: m.TestimonialsSection })));
const CtaBanner = lazy(() => import("@/components/sections/CtaBanner").then((m) => ({ default: m.CtaBanner })));
const SiteFooter = lazy(() => import("@/components/layout/SiteFooter").then((m) => ({ default: m.SiteFooter })));

export const Route = createFileRoute("/")({
  head: () => {
    const seo = buildSeo({
      title: "BAZZAR 3D | Web & App Design Agency in Saudi Arabia",
      description:
        "BAZZAR 3D is a Saudi digital agency specialized in web & app design, e-commerce, branding, and digital marketing. بازار ثرى دى وكالة رقمية سعودية متخصصة في تصميم وتطوير المواقع وتطبيقات الجوال والمتاجر الإلكترونية والهوية البصرية والتسويق الرقمي.",
      keywords:
        "web design, app design, e-commerce, branding, digital marketing, SEO, Saudi Arabia, Riyadh, BAZZAR 3D, تصميم مواقع, تطوير مواقع, تصميم تطبيقات, متجر إلكتروني, هوية بصرية, تسويق رقمي, سيو, السعودية, الرياض, بازار ثرى دى",
      path: "/",
    });
    return {
      meta: seo.meta,
      links: seo.links,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(organizationJsonLd()) },
        { type: "application/ld+json", children: JSON.stringify(websiteJsonLd()) },
      ],
    };
  },
  component: Index,
});

function Index() {
  const { dir } = useLang();

  return (
    <div dir={dir} className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <HeroSection />
        <LazyHomeSection variant="up"><AboutIntroSection /></LazyHomeSection>
        <LazyHomeSection variant="up" delay={80}><ServicesGrid /></LazyHomeSection>
        <LazyHomeSection variant="up"><PortfolioSection /></LazyHomeSection>
        <LazyHomeSection variant="left"><WhyUsSection /></LazyHomeSection>
        <LazyHomeSection variant="right"><StrategySection /></LazyHomeSection>
        <LazyHomeSection variant="up"><ProcessSection /></LazyHomeSection>
        <LazyHomeSection variant="zoom"><StatsBanner /></LazyHomeSection>
        <LazyHomeSection variant="up"><ActionCardsSection /></LazyHomeSection>
        <LazyHomeSection variant="up"><TestimonialsSection /></LazyHomeSection>
        <LazyHomeSection variant="zoom"><CtaBanner /></LazyHomeSection>
      </main>
      <LazyFooter />
    </div>
  );
}

function LazyHomeSection({ children, variant, delay }: { children: ReactNode; variant: "up" | "left" | "right" | "zoom"; delay?: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shouldRender) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "350px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={ref} className={shouldRender ? undefined : "min-h-32"}>
      {shouldRender && (
        <Suspense fallback={<div className="min-h-32" aria-hidden="true" />}>
          <Reveal variant={variant} delay={delay}>{children}</Reveal>
        </Suspense>
      )}
    </div>
  );
}

function LazyFooter() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shouldRender) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={ref} className={shouldRender ? undefined : "min-h-24"}>
      {shouldRender && (
        <Suspense fallback={null}>
          <SiteFooter />
        </Suspense>
      )}
    </div>
  );
}

